import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Autocomplete,
  TextField,
  Grid,
  Chip,
  IconButton,
  Button,
  Stack,
  InputAdornment,
  Paper,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import PropTypes from "prop-types";
import { API_ENDPOINT } from "../../../constants/constants";
import { getDbHeader } from "../../../utils2024/utils";

const CategorySelectorAccordions = ({
  title = "",
  onSelectionChange,
  selectedCategories: propSelectedCategories = null,
  isOpen = false,
  maxSelections = 15,
}) => {
  const [productCategories, setProductCategories] = useState([]);
  const [categoryType, setCategoryType] = useState(null); // "single" | "double" | "triple"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMains, setExpandedMains] = useState({});

  // Controlled vs uncontrolled mode
  const [internalSelectedCategories, setInternalSelectedCategories] =
    useState([]);
  const isControlled = propSelectedCategories !== null;
  const selectedCategories = isControlled
    ? propSelectedCategories
    : internalSelectedCategories;

  // Fetch categories when component mounts/opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_ENDPOINT}/event/get-product-categories`,
          {
            method: "GET",
            ...getDbHeader(),
          }
        );

        if (!response.ok) throw new Error("Failed to load categories");
        const data = await response.json();

        setCategoryType(data?.type || null);
        setProductCategories(data?.categories || []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [isOpen]);

  // Shared selection updater
  const applySelection = useCallback(
    (newSelections) => {
      if (!isControlled) {
        setInternalSelectedCategories(newSelections);
      }
      onSelectionChange?.(newSelections);
    },
    [isControlled, onSelectionChange]
  );

  // SINGLE type options for Autocomplete
  const singleOptions = useMemo(() => {
    if (categoryType !== "single" || !productCategories.length) return [];
    const seen = new Set();
    return productCategories.reduce((acc, cat) => {
      const main = cat.MainCategory?.trim();
      if (main && !seen.has(main)) {
        seen.add(main);
        acc.push({ MainCategory: main, Category: null, SubCategory: null });
      }
      return acc;
    }, []);
  }, [categoryType, productCategories]);

  // Current Autocomplete value for SINGLE type
  const singleValue = useMemo(
    () =>
      selectedCategories.filter(
        (sel) => sel.Category === null && sel.SubCategory === null
      ),
    [selectedCategories]
  );

  const handleSingleChange = useCallback(
    (_event, newValue) => {
      if (newValue && newValue.length > maxSelections) return;
      applySelection(newValue);
    },
    [applySelection, maxSelections]
  );

  // DOUBLE / TRIPLE types: Group data into Main -> Category -> Subcategory
  const groupedData = useMemo(() => {
    if (categoryType === "single" || !productCategories.length) return {};

    return productCategories.reduce((acc, curr) => {
      const mainCategory = curr.MainCategory?.trim();
      const category = curr.Category?.trim();
      const subCategory = curr.SubCategory?.trim();

      if (!mainCategory) return acc;

      if (!acc[mainCategory]) {
        acc[mainCategory] = { name: mainCategory, categories: {} };
      }

      if (category) {
        if (!acc[mainCategory].categories[category]) {
          acc[mainCategory].categories[category] = {
            name: category,
            subcategories: [],
          };
        }

        if (subCategory) {
          acc[mainCategory].categories[category].subcategories.push({
            name: subCategory,
          });
        }
      }

      return acc;
    }, {});
  }, [categoryType, productCategories]);

  // Filtered Grouped Data based on Search Query
  const filteredGroupedData = useMemo(() => {
    if (!searchQuery.trim()) return groupedData;
    const query = searchQuery.toLowerCase().trim();
    const result = {};

    Object.entries(groupedData).forEach(([mainName, mainObj]) => {
      const mainMatches = mainName.toLowerCase().includes(query);
      const filteredCategories = {};

      Object.entries(mainObj.categories).forEach(([catName, catObj]) => {
        const catMatches = catName.toLowerCase().includes(query);
        const filteredSubcategories = catObj.subcategories.filter((sub) =>
          sub.name.toLowerCase().includes(query)
        );

        if (mainMatches || catMatches || filteredSubcategories.length > 0) {
          filteredCategories[catName] = {
            ...catObj,
            subcategories:
              mainMatches || catMatches
                ? catObj.subcategories
                : filteredSubcategories,
          };
        }
      });

      if (mainMatches || Object.keys(filteredCategories).length > 0) {
        result[mainName] = {
          ...mainObj,
          categories: filteredCategories,
        };
      }
    });

    return result;
  }, [groupedData, searchQuery]);

  // Count selections per main category
  const selectedCountsByMain = useMemo(() => {
    const counts = {};
    if (!Array.isArray(selectedCategories)) return counts;

    selectedCategories.forEach((sel) => {
      const main = sel.mainCat || sel.MainCategory;
      if (main) {
        counts[main] = (counts[main] || 0) + 1;
      }
    });
    return counts;
  }, [selectedCategories]);

  // Check subcategory selection
  const isSubcategorySelected = useCallback(
    (sub, mainName, categoryName) =>
      selectedCategories.some(
        (sel) =>
          (sel.mainCat === mainName || sel.MainCategory === mainName) &&
          (sel.subCat === categoryName || sel.Category === categoryName) &&
          (sel.subSubCat === sub.name || sel.SubCategory === sub.name)
      ),
    [selectedCategories]
  );

  // Toggle selection
  const handleCheckboxChange = useCallback(
    (sub, mainName, categoryName) => {
      const catObj = {
        MainCategory: mainName,
        Category: categoryName,
        SubCategory: sub.name,
        mainCat: mainName,
        subCat: categoryName,
        subSubCat: sub.name,
      };

      const prev = isControlled
        ? propSelectedCategories
        : internalSelectedCategories;

      const exists = prev.some(
        (sel) =>
          (sel.mainCat === mainName || sel.MainCategory === mainName) &&
          (sel.subCat === categoryName || sel.Category === categoryName) &&
          (sel.subSubCat === sub.name || sel.SubCategory === sub.name)
      );

      if (!exists && prev.length >= maxSelections) {
        alert(`Maximum ${maxSelections} category selections allowed.`);
        return;
      }

      applySelection(
        exists
          ? prev.filter(
              (sel) =>
                !(
                  (sel.mainCat === mainName || sel.MainCategory === mainName) &&
                  (sel.subCat === categoryName || sel.Category === categoryName) &&
                  (sel.subSubCat === sub.name || sel.SubCategory === sub.name)
                )
            )
          : [...prev, catObj]
      );
    },
    [
      isControlled,
      propSelectedCategories,
      internalSelectedCategories,
      applySelection,
      maxSelections,
    ]
  );

  // Handle Expand / Collapse all
  const handleToggleExpandAll = (expand) => {
    const newState = {};
    Object.keys(groupedData).forEach((mainName) => {
      newState[mainName] = expand;
    });
    setExpandedMains(newState);
  };

  const handleAccordionToggle = (mainName) => (event, isExpanded) => {
    setExpandedMains((prev) => ({ ...prev, [mainName]: isExpanded }));
  };

  if (!isOpen) return null;

  if (loading)
    return (
      <Box textAlign="center" py={4}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="textSecondary" mt={1}>
          Loading product categories...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error.message || "Failed to load categories"}
      </Alert>
    );

  if (!productCategories.length)
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        No categories available
      </Alert>
    );

  return (
    <Box my={2}>
      {/* Header bar with Search and Expand All controls */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Search Input */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search categories or subcategories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#ffffff",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Quick Controls & Selection Badge */}
          {categoryType !== "single" && (
            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              <Chip
                label={`Selected: ${selectedCategories.length} / ${maxSelections}`}
                color={
                  selectedCategories.length > 0 ? "primary" : "default"
                }
                variant="outlined"
                size="small"
                sx={{ fontWeight: 700, borderRadius: "8px" }}
              />
              <Tooltip title="Expand All">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleToggleExpandAll(true)}
                  startIcon={<UnfoldMoreIcon fontSize="small" />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Expand
                </Button>
              </Tooltip>
              <Tooltip title="Collapse All">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleToggleExpandAll(false)}
                  startIcon={<UnfoldLessIcon fontSize="small" />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Collapse
                </Button>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Categories Tree */}
      {categoryType === "single" ? (
        <Autocomplete
          multiple
          limitTags={5}
          disableCloseOnSelect
          options={singleOptions}
          value={singleValue}
          getOptionLabel={(option) => option.MainCategory}
          isOptionEqualToValue={(option, value) =>
            option.MainCategory === value.MainCategory
          }
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} style={{ marginRight: 8 }} />
              {option.MainCategory}
            </li>
          )}
          onChange={handleSingleChange}
          renderInput={(params) => (
            <TextField {...params} label="Select Main Categories" />
          )}
        />
      ) : (
        /* Accordion Tree with Max Height and Custom Scrollbar */
        <Box
          sx={{
            maxHeight: 460,
            overflowY: "auto",
            pr: 1,
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            p: 1.5,
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#cbd5e1",
              borderRadius: 3,
            },
          }}
        >
          {Object.keys(filteredGroupedData).length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="textSecondary">
                No matching categories found for "{searchQuery}"
              </Typography>
            </Box>
          ) : (
            Object.values(filteredGroupedData).map((mainCategory) => {
              const count = selectedCountsByMain[mainCategory.name] || 0;
              const isSearching = Boolean(searchQuery.trim());
              const isExpanded =
                expandedMains[mainCategory.name] !== undefined
                  ? expandedMains[mainCategory.name]
                  : isSearching;

              return (
                <Accordion
                  key={mainCategory.name}
                  expanded={isExpanded}
                  onChange={handleAccordionToggle(mainCategory.name)}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: count > 0 ? "1px solid #93c5fd" : "1px solid #e2e8f0",
                    borderRadius: "12px !important",
                    backgroundColor: count > 0 ? "#f0f7ff" : "#f8fafc",
                    "&:before": { display: "none" },
                    overflow: "hidden",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#475569" }} />}
                    sx={{
                      px: 2,
                      py: 0.5,
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        mr: 1,
                      },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
                      {mainCategory.name}
                    </Typography>
                    {count > 0 && (
                      <Chip
                        label={`${count} selected`}
                        size="small"
                        color="primary"
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          borderRadius: "6px",
                          ml: 2,
                        }}
                      />
                    )}
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 1, bgcolor: "#ffffff" }}>
                    {Object.values(mainCategory.categories).map((category) => (
                      <Box key={category.name} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          color="#0288d1"
                          sx={{ mb: 1, pb: 0.5, borderBottom: "1px solid #f1f5f9" }}
                        >
                          {category.name}
                        </Typography>

                        {/* 2-Column Responsive Grid for Subcategories */}
                        <Grid container spacing={0.5}>
                          {category.subcategories.map((sub) => {
                            const selected = isSubcategorySelected(
                              sub,
                              mainCategory.name,
                              category.name
                            );
                            return (
                              <Grid item xs={12} sm={6} key={sub.name}>
                                <FormControlLabel
                                  sx={{
                                    width: "100%",
                                    m: 0,
                                    py: 0.25,
                                    px: 0.5,
                                    borderRadius: "8px",
                                    backgroundColor: selected
                                      ? "#eff6ff"
                                      : "transparent",
                                    "&:hover": { backgroundColor: "#f1f5f9" },
                                  }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selected}
                                      onChange={() =>
                                        handleCheckboxChange(
                                          sub,
                                          mainCategory.name,
                                          category.name
                                        )
                                      }
                                      sx={{ p: 0.5, mr: 0.5 }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      fontSize="0.85rem"
                                      color={selected ? "#0f172a" : "#475569"}
                                      fontWeight={selected ? 700 : 400}
                                    >
                                      {sub.name}
                                    </Typography>
                                  }
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
};

CategorySelectorAccordions.propTypes = {
  title: PropTypes.string,
  onSelectionChange: PropTypes.func,
  selectedCategories: PropTypes.array,
  isOpen: PropTypes.bool,
  maxSelections: PropTypes.number,
};

export default React.memo(CategorySelectorAccordions);
